import type { AudienceInput, SystemAccessInput, SystemAccessView } from '@ralph/contracts'
import { Ban, Globe, TriangleAlert, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import { AudiencePicker } from '@/components/groups/AudiencePicker.tsx'
import type { PickerViewer } from '@/components/groups/audience-picker.ts'
import { GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { Notice } from '@/components/ui/admin-chrome.tsx'
import { Button, Panel } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useI18n, useT } from '@/i18n/index.ts'

/**
 * Who can use a connected system or a remote agent (PRD-26 D-25, GRP-060, GRP-061).
 *
 * Two kinds only — everyone who signs in, or specific groups — and one more state the reader must
 * never have to infer: *groups* with none left reaches nobody. That happens when the last of its
 * groups is deleted, or when migration 0013 closed a list of token group names that meant nothing
 * here. It is fail-closed on the server, and it is said in words here, with the glyph for "no",
 * because an empty list of chips reads as "nothing configured" — the opposite of what it means.
 */

/** "Everyone who signs in" · "Only people in Finance, Legal +2" · "Nobody can use it". */
export const AccessSummary = ({ access, linked = false }: { access: SystemAccessView; linked?: boolean }) => {
  // Read here rather than at module scope: this file used to open with
  // `const t = systemsStrings.access`, which freezes both the words and the formatter at
  // import time and then renders the previous language after a switch, with no type error,
  // no runtime error and no failing test (PRD-27 F-3). Hooks run before the early returns
  // below, as they must.
  const fmt = useI18n()
  const t = fmt.t.admin.systems.access

  if (access.kind === 'everyone') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Globe size={12} strokeWidth={2.1} aria-hidden className="shrink-0" />
        {t.everyone}
      </span>
    )
  }

  if (access.groups.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-alert">
        <Ban size={12} strokeWidth={2.2} aria-hidden className="shrink-0" />
        {t.nobody}
      </span>
    )
  }

  const SHOWN = linked ? access.groups.length : 3
  const rest = access.groups.length - SHOWN
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <UsersRound size={12} strokeWidth={2.1} aria-hidden className="shrink-0" />
      <span className="sr-only">{t.onlyGroups}</span>
      {access.groups.slice(0, SHOWN).map((group) => (
        <GroupLabel key={group.id} group={group} size={16} to={linked} className="max-w-[12rem]" />
      ))}
      {rest > 0 && (
        <span className="readout text-fg-subtle">
          <span aria-hidden>{t.more(rest)}</span>
          <span className="sr-only">{t.moreLabel(fmt, rest)}</span>
        </span>
      )}
    </span>
  )
}

const inputOf = (access: SystemAccessView): AudienceInput => ({
  kind: access.kind,
  groupIds: access.kind === 'groups' ? access.groups.map((group) => group.id) : []
})

const sameAccess = (a: AudienceInput, b: AudienceInput): boolean =>
  a.kind === b.kind &&
  a.groupIds.length === b.groupIds.length &&
  [...a.groupIds].sort().join() === [...b.groupIds].sort().join()

/**
 * The editor: the summary, and behind *Change* the shared picker in its two-kind mode.
 *
 * Saving is bound to the version that was read (the caller's `onSave` sends `If-Match`), and a
 * `precondition_failed` answer is the one refusal this editor explains itself — somebody else
 * changed the item meanwhile. It asks the caller for the current row rather than retrying, so
 * the administrator chooses again against what is really there.
 */
export const SystemAccessEditor = ({
  name,
  access,
  saving,
  onSave,
  onStale
}: {
  /** The system's name, for the Change button's accessible name. */
  name: string
  access: SystemAccessView
  saving: boolean
  onSave: (next: SystemAccessInput, handlers: { onSuccess: () => void; onError: (error: unknown) => void }) => void
  onStale: () => void
}) => {
  const words = useT()
  const t = words.admin.systems.access
  const session = useSession()
  const user = session.data?.user
  const viewer: PickerViewer = {
    isAdmin: user?.roles.includes('admin') ?? false,
    groupIds: user?.groups.map((group) => group.id) ?? [],
    managedGroupIds: user?.managedGroups.map((group) => group.id) ?? []
  }

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<AudienceInput>(() => inputOf(access))
  const [outcome, setOutcome] = useState<'saved' | 'stale' | 'failed' | null>(null)

  const live = inputOf(access)
  const unchanged = sameAccess(draft, live)
  const incomplete = draft.kind === 'groups' && draft.groupIds.length === 0
  const nobody = access.kind === 'groups' && access.groups.length === 0

  const open = () => {
    setDraft(inputOf(access))
    setOutcome(null)
    setEditing(true)
  }

  const save = () => {
    const next: SystemAccessInput =
      draft.kind === 'groups' ? { kind: 'groups', groupIds: draft.groupIds } : { kind: 'everyone', groupIds: [] }
    onSave(next, {
      onSuccess: () => {
        setOutcome('saved')
        setEditing(false)
      },
      onError: (error) => {
        if (error instanceof ApiRequestError && error.code === 'precondition_failed') {
          setOutcome('stale')
          setEditing(false)
          onStale()
        } else {
          setOutcome('failed')
        }
      }
    })
  }

  return (
    <div>
      {editing ? (
        <>
          <AudiencePicker
            mode="system"
            value={draft}
            onChange={setDraft}
            live={live}
            known={access.groups}
            viewer={viewer}
            privateLabel=""
            consequence={words.groups.consequence.system}
            disabled={saving}
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="primary" disabled={saving || unchanged || incomplete} onClick={save}>
              {saving ? t.saving : t.save}
            </Button>
            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
              {t.cancel}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px]">
          <div className="min-w-0 flex-1 basis-[14rem]">
            <span className="sr-only">{t.label} </span>
            <AccessSummary access={access} linked />
          </div>
          {viewer.isAdmin && (
            <Button size="sm" aria-label={t.changeLabel(name)} onClick={open}>
              {t.change}
            </Button>
          )}
        </div>
      )}

      {!editing && nobody && <p className="mt-1.5 text-[12.5px] leading-snug text-fg-muted">{t.nobodyHint}</p>}

      {outcome === 'saved' && (
        <p role="status" className="mt-2.5 text-[12.5px] text-fg-muted">
          {t.saved}
        </p>
      )}
      {(outcome === 'stale' || outcome === 'failed') && (
        <div role="alert" className="mt-3">
          <Notice
            title={outcome === 'stale' ? t.stale : t.failed}
            icon={<TriangleAlert size={12} strokeWidth={2.1} aria-hidden />}
          />
        </div>
      )}
    </div>
  )
}

/** The editor as a system page's own section: a titled panel with what the setting governs. */
export const SystemAccessPanel = ({ lead, ...editor }: Parameters<typeof SystemAccessEditor>[0] & { lead: string }) => {
  const t = useT().admin.systems.access

  return (
    <Panel className="px-4 py-3.5">
      <h3 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.title}</h3>
      <p className="mt-0.5 text-[12.5px] leading-snug text-fg-muted">{lead}</p>
      <div className="mt-3">
        <SystemAccessEditor {...editor} />
      </div>
    </Panel>
  )
}
